import React, { useState, useEffect, useCallback } from 'react';
import { 
  Briefcase, 
  ToggleLeft, 
  ToggleRight, 
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Info,
  Plus,
  Trash2,
  Edit2,
  X,
  Save,
  AlertTriangle
} from 'lucide-react';

const AVAILABLE_ICONS = [
  'ShoppingCart', 'Package', 'Truck', 'Wrench', 'FileText', 'Users', 
  'Briefcase', 'Building', 'Monitor', 'Cpu', 'HardDrive', 'Headphones',
  'Phone', 'Mail', 'Settings', 'Tool', 'Box', 'Archive'
];

const PositionsManagement = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);
  const [activeCount, setActiveCount] = useState(0);
  const [maxActive] = useState(6);
  
  const [modalMode, setModalMode] = useState(null); // null, 'add', 'edit'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [editingPositionKey, setEditingPositionKey] = useState(null);
  
  // Form state - separate from modal visibility
  const [formPositionKey, setFormPositionKey] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formSubtitle, setFormSubtitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formRequirements, setFormRequirements] = useState(['']);
  const [formBenefits, setFormBenefits] = useState(['']);
  const [formIcon, setFormIcon] = useState('Briefcase');
  const [formEnabled, setFormEnabled] = useState(true);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPositions();
  }, []);

  const fetchPositions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/backend/api/job_applications.php?path=all_positions', { credentials: 'include' });
      const data = await response.json();

      if (data.success) {
        setPositions(data.positions || []);
        setActiveCount(data.activeCount || 0);
      } else {
        setError(data.error || 'API returned success=false');
        setPositions([]);
      }
    } catch (err) {
      console.error('Greška pri učitavanju pozicija:', err);
      setError(err.message || 'Unknown fetch error');
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePosition = async (positionKey, currentStatus) => {
    if (!currentStatus && activeCount >= maxActive) {
      alert('Maksimalno 6 aktivnih pozicija. Deaktivirajte neku postojeću poziciju prvo.');
      return;
    }
    
    setUpdating(prev => ({ ...prev, [positionKey]: true }));
    
    try {
      const response = await fetch('/backend/api/job_applications.php?path=position/toggle', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ position_key: positionKey })
      });

      const data = await response.json();
      
      if (data.success) {
        setPositions(prev => prev.map(pos => 
          pos.position_key === positionKey 
            ? { ...pos, enabled: !currentStatus }
            : pos
        ));
        setActiveCount(prev => currentStatus ? prev - 1 : prev + 1);
      } else {
        alert('Greška: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (err) {
      console.error('Greška:', err);
      alert('Greška pri ažuriranju pozicije');
    } finally {
      setUpdating(prev => ({ ...prev, [positionKey]: false }));
    }
  };

  const resetForm = useCallback(() => {
    setFormPositionKey('');
    setFormTitle('');
    setFormSubtitle('');
    setFormDescription('');
    setFormRequirements(['']);
    setFormBenefits(['']);
    setFormIcon('Briefcase');
    setFormEnabled(true);
    setFormError('');
  }, []);

  const openAddModal = () => {
    resetForm();
    setEditingPositionKey(null);
    setModalMode('add');
  };

  const openEditModal = (position) => {
    setEditingPositionKey(position.position_key);
    setFormPositionKey(position.position_key);
    setFormTitle(position.title || '');
    setFormSubtitle(position.subtitle || '');
    setFormDescription(position.description || '');
    setFormRequirements(
      Array.isArray(position.requirements) && position.requirements.length > 0 
        ? [...position.requirements] 
        : ['']
    );
    setFormBenefits(
      Array.isArray(position.benefits) && position.benefits.length > 0 
        ? [...position.benefits] 
        : ['']
    );
    setFormIcon(position.icon || 'Briefcase');
    setFormEnabled(position.enabled);
    setFormError('');
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingPositionKey(null);
  };

  const handleRequirementChange = (index, value) => {
    setFormRequirements(prev => prev.map((item, i) => i === index ? value : item));
  };

  const handleBenefitChange = (index, value) => {
    setFormBenefits(prev => prev.map((item, i) => i === index ? value : item));
  };

  const addRequirement = () => {
    setFormRequirements(prev => [...prev, '']);
  };

  const addBenefit = () => {
    setFormBenefits(prev => [...prev, '']);
  };

  const removeRequirement = (index) => {
    setFormRequirements(prev => prev.filter((_, i) => i !== index));
  };

  const removeBenefit = (index) => {
    setFormBenefits(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const isEdit = modalMode === 'edit';
    setFormError('');
    
    if (!formTitle.trim()) {
      setFormError('Naziv pozicije je obavezan');
      return;
    }
    
    if (!isEdit && !formPositionKey.trim()) {
      setFormError('Ključ pozicije je obavezan');
      return;
    }

    if (!isEdit && formEnabled && activeCount >= maxActive) {
      setFormError('Maksimalno 6 aktivnih pozicija. Kreirajte poziciju kao neaktivnu ili deaktivirajte neku postojeću.');
      return;
    }

    setSaving(true);
    
    try {
      const payload = {
        position_key: formPositionKey.trim(),
        title: formTitle.trim(),
        subtitle: formSubtitle.trim(),
        description: formDescription.trim(),
        requirements: formRequirements.filter(r => r.trim()),
        benefits: formBenefits.filter(b => b.trim()),
        icon: formIcon,
        enabled: formEnabled
      };

      const response = await fetch('/backend/api/job_applications.php?path=position', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data.success) {
        fetchPositions();
        closeModal();
        resetForm();
      } else {
        setFormError(data.error || 'Greška pri čuvanju pozicije');
      }
    } catch (err) {
      console.error('Greška:', err);
      setFormError('Greška pri komunikaciji sa serverom');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (positionKey) => {
    try {
      const response = await fetch(`/backend/api/job_applications.php?path=position&key=${encodeURIComponent(positionKey)}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        fetchPositions();
        setShowDeleteConfirm(null);
      } else {
        alert('Greška: ' + (data.error || 'Nepoznata greška'));
      }
    } catch (err) {
      console.error('Greška:', err);
      alert('Greška pri brisanju pozicije');
    }
  };

  const getStatusBadge = (enabled) => {
    if (enabled) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
          <CheckCircle className="w-4 h-4" />
          Aktivna
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-200">
        <XCircle className="w-4 h-4" />
        Neaktivna
      </span>
    );
  };

  const getToggleIcon = (enabled, positionKey) => {
    const isUpdating = updating[positionKey];
    
    if (isUpdating) {
      return <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />;
    }
    
    if (enabled) {
      return <ToggleRight className="w-6 h-6 text-green-600 hover:text-green-700" />;
    }
    return <ToggleLeft className="w-6 h-6 text-gray-400 hover:text-gray-500" />;
  };

  const totalPositions = positions.length;
  const isEdit = modalMode === 'edit';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Upravljanje pozicijama</h2>
          <p className="text-muted-foreground mt-1">
            Kontroliši dostupnost pozicija na sajtu za prijave
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchPositions}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Osveži
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Dodaj poziciju
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Briefcase className="w-4 h-4 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Ukupno pozicija</p>
              <p className="text-2xl font-bold text-foreground">{totalPositions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Eye className="w-4 h-4 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Aktivne pozicije</p>
              <p className="text-2xl font-bold text-foreground">{activeCount} / {maxActive}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <EyeOff className="w-4 h-4 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-muted-foreground">Neaktivne pozicije</p>
              <p className="text-2xl font-bold text-foreground">{totalPositions - activeCount}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              Kako funkcioniše upravljanje pozicijama?
            </h3>
            <p className="text-sm text-blue-700">
              • <strong>Maksimalno 6 aktivnih pozicija</strong> - pozicije se prikazuju na heksagonima na sajtu<br/>
              • Ako imate manje od 6 aktivnih pozicija, one se ponavljaju da popune svih 6 heksagona<br/>
              • Možete imati više od 6 pozicija u sistemu, ali samo 6 može biti aktivno istovremeno<br/>
              • <strong>Aktivne pozicije</strong> se prikazuju na sajtu i korisnici mogu da se prijave za njih
            </p>
          </div>
        </div>
      </div>

      <div className="bg-background border border-border rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Lista pozicija</h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Učitavanje pozicija...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Greška pri učitavanju pozicija</h3>
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : positions.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">Nema pozicija</h3>
            <p className="text-muted-foreground">Trenutno nema definisanih pozicija u sistemu.</p>
            <button
              onClick={openAddModal}
              className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              Dodaj prvu poziciju
            </button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {positions.map((position) => (
              <div key={position.position_key} className="px-6 py-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-lg font-medium text-foreground">
                            {position.title}
                          </h3>
                          <span className="text-xs px-2 py-0.5 bg-gray-100 rounded text-gray-600">
                            {position.icon || 'Briefcase'}
                          </span>
                        </div>
                        {position.subtitle && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {position.subtitle}
                          </p>
                        )}
                        <div className="flex items-center gap-3">
                          <span className="text-xs px-2 py-1 bg-muted rounded-md text-muted-foreground font-mono">
                            {position.position_key}
                          </span>
                          {getStatusBadge(position.enabled)}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditModal(position)}
                          className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                          title="Izmeni poziciju"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => setShowDeleteConfirm(position.position_key)}
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600 transition-colors"
                          title="Obriši poziciju"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => togglePosition(position.position_key, position.enabled)}
                          disabled={updating[position.position_key]}
                          className="p-2 rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                          title={position.enabled ? 'Deaktiviraj poziciju' : 'Aktiviraj poziciju'}
                        >
                          {getToggleIcon(position.enabled, position.position_key)}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Position Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-xl font-bold text-gray-900">
                {isEdit ? 'Izmeni poziciju' : 'Dodaj novu poziciju'}
              </h3>
              <button 
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {formError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {formError}
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ključ pozicije (ID) *
                  </label>
                  <input
                    type="text"
                    value={formPositionKey}
                    onChange={(e) => setFormPositionKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                    disabled={isEdit}
                    placeholder="npr. prodavac"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ikona
                  </label>
                  <select
                    value={formIcon}
                    onChange={(e) => setFormIcon(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {AVAILABLE_ICONS.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Naziv pozicije *
                </label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="npr. Prodavac IT opreme"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Podnaslov
                </label>
                <input
                  type="text"
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                  placeholder="npr. Prodaja i savetovanje klijenata"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opis
                </label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Opišite poziciju..."
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zahtevi
                </label>
                {formRequirements.map((req, index) => (
                  <div key={`req-${index}`} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={req}
                      onChange={(e) => handleRequirementChange(index, e.target.value)}
                      placeholder={`Zahtev ${index + 1}`}
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {formRequirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRequirement(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addRequirement}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Dodaj zahtev
                </button>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Benefiti
                </label>
                {formBenefits.map((benefit, index) => (
                  <div key={`ben-${index}`} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={benefit}
                      onChange={(e) => handleBenefitChange(index, e.target.value)}
                      placeholder={`Benefit ${index + 1}`}
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                    {formBenefits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeBenefit(index)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addBenefit}
                  className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Dodaj benefit
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formEnabled}
                    onChange={(e) => setFormEnabled(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Aktivna pozicija (prikazuje se na sajtu)</span>
                </label>
              </div>
              
              {!isEdit && formEnabled && activeCount >= maxActive && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>Već imate {activeCount} aktivnih pozicija. Morate deaktivirati neku postojeću poziciju ili kreirati ovu kao neaktivnu.</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isEdit ? 'Sačuvaj izmene' : 'Dodaj poziciju'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Potvrdi brisanje</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Da li ste sigurni da želite da obrišete ovu poziciju? Ova akcija se ne može poništiti.
              <br/><br/>
              <strong>Napomena:</strong> Pozicija se ne može obrisati ako postoje prijave za nju.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Otkaži
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Obriši
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionsManagement;
